"use client";

import {
  IconAlertInfoOutline18,
  IconCheckOutline18,
  IconPlusOutline18,
  IconTrashOutline18,
  IconXmarkOutline18,
} from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import JsonEditorBase from "./JsonEditorBase";

// --- Types ---

interface FilterBuilderProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  // eventAbi or fields supplies the field picker + operator constraints.
  // - eventAbi: EVM-log event ABI (parses .inputs into fields)
  // - fields: direct field list, used by enrichmentFilter where there's no ABI
  eventAbi?: string;
  fields?: EventAbiInput[];
}

export interface EventAbiInput {
  name: string;
  type: string;
  indexed?: boolean;
}

interface Condition {
  field: string;
  operator: string;
  value: string;
}

// --- Operator config by ABI type category ---

type AbiTypeCategory = "string" | "bigint" | "boolean";

const OPERATORS_BY_CATEGORY: Record<AbiTypeCategory, { value: string; label: string }[]> = {
  string: [
    { value: "$eq", label: "=" },
    { value: "$ne", label: "\u2260" },
    { value: "$in", label: "in" },
    { value: "$nin", label: "not in" },
  ],
  bigint: [
    { value: "$gte", label: "\u2265" },
    { value: "$gt", label: ">" },
    { value: "$eq", label: "=" },
    { value: "$ne", label: "\u2260" },
    { value: "$lt", label: "<" },
    { value: "$lte", label: "\u2264" },
    { value: "$in", label: "in" },
    { value: "$nin", label: "not in" },
  ],
  boolean: [
    { value: "$eq", label: "=" },
    { value: "$ne", label: "\u2260" },
  ],
};

const ALL_OPERATORS = [
  { value: "$eq", label: "=" },
  { value: "$ne", label: "\u2260" },
  { value: "$gt", label: ">" },
  { value: "$gte", label: "\u2265" },
  { value: "$lt", label: "<" },
  { value: "$lte", label: "\u2264" },
  { value: "$in", label: "in" },
  { value: "$nin", label: "not in" },
];

// --- Helpers ---

function abiTypeToCategory(abiType: string): AbiTypeCategory {
  if (/^u?int\d*$/.test(abiType)) return "bigint";
  if (abiType === "bool") return "boolean";
  return "string"; // address, string, bytes, bytes32, etc.
}

function parseEventAbiInputs(eventAbiJson: string | undefined): EventAbiInput[] {
  if (!eventAbiJson) return [];
  try {
    const parsed = typeof eventAbiJson === "string" ? JSON.parse(eventAbiJson) : eventAbiJson;
    if (parsed?.inputs && Array.isArray(parsed.inputs)) {
      return parsed.inputs;
    }
  } catch {
    // Invalid JSON
  }
  return [];
}

function getOperatorsForField(fieldName: string, fields: EventAbiInput[]): { value: string; label: string }[] {
  const field = fields.find(f => f.name === fieldName);
  if (!field) return ALL_OPERATORS;
  const category = abiTypeToCategory(field.type);
  return OPERATORS_BY_CATEGORY[category] || ALL_OPERATORS;
}

// --- Parse DSL → conditions ---

function parseDslToConditions(jsonString: string): {
  logicalOp: "$and" | "$or";
  conditions: Condition[];
} | null {
  try {
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== "object") return null;

    const logicalOp: "$and" | "$or" = parsed.$or ? "$or" : "$and";
    const items = parsed[logicalOp];
    if (!Array.isArray(items)) return null;

    const conditions: Condition[] = [];
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const field = Object.keys(item)[0];
      if (!field) continue;
      const fieldObj = item[field];
      if (!fieldObj || typeof fieldObj !== "object") continue;
      const operator = Object.keys(fieldObj)[0];
      if (!operator) continue;
      const val = fieldObj[operator];
      conditions.push({
        field,
        operator,
        value: typeof val === "string" ? val : JSON.stringify(val),
      });
    }

    return { logicalOp, conditions };
  } catch {
    return null;
  }
}

// --- Conditions → DSL ---

function conditionsToDsl(logicalOp: "$and" | "$or", conditions: Condition[]): string {
  const items = conditions
    .filter(c => c.field)
    .map(c => {
      let val: unknown = c.value;
      // Try to parse as JSON for $in/$nin arrays or numbers
      if (c.operator === "$in" || c.operator === "$nin") {
        try {
          val = JSON.parse(c.value);
        } catch {
          // Keep as string
        }
      }
      return { [c.field]: { [c.operator]: val } };
    });

  if (items.length === 0) {
    return JSON.stringify({ [logicalOp]: [] }, null, 2);
  }

  return JSON.stringify({ [logicalOp]: items }, null, 2);
}

// --- Validation ---

function validateFilterDSL(jsonString: string): { valid: boolean; error?: string } {
  try {
    const parsed = JSON.parse(jsonString);
    if (!parsed.$and && !parsed.$or) {
      return { valid: false, error: "Top level must contain $and or $or" };
    }

    const validOperators = ["$eq", "$ne", "$gt", "$gte", "$lt", "$lte", "$in", "$nin"];

    const checkConditions = (conditions: unknown): boolean => {
      if (!Array.isArray(conditions)) return false;
      if (conditions.length === 0) return true; // Empty is valid
      return conditions.every((condition: unknown) => {
        if (!condition || typeof condition !== "object") return false;
        const c = condition as Record<string, unknown>;
        if (c.$and || c.$or) {
          return checkConditions(c.$and || c.$or);
        }
        const keys = Object.keys(c);
        return keys.some(key => {
          if (typeof c[key] === "object" && c[key] !== null) {
            return Object.keys(c[key] as Record<string, unknown>).some(op => validOperators.includes(op));
          }
          return false;
        });
      });
    };

    const conditions = parsed.$and || parsed.$or;
    if (Array.isArray(conditions) && conditions.length > 0 && !checkConditions(conditions)) {
      return { valid: false, error: "Invalid filter condition structure" };
    }

    return { valid: true };
  } catch (e) {
    return { valid: false, error: e instanceof Error ? e.message : "Invalid JSON" };
  }
}

// --- Component ---

export default function FilterBuilder({
  label,
  description,
  value,
  onChange,
  eventAbi,
  fields: fieldsProp,
}: FilterBuilderProps) {
  const fields = useMemo(
    () => (fieldsProp && fieldsProp.length > 0 ? fieldsProp : parseEventAbiInputs(eventAbi)),
    [fieldsProp, eventAbi],
  );

  const [logicalOp, setLogicalOp] = useState<"$and" | "$or">("$and");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [showRawJson, setShowRawJson] = useState(false);
  const isRawEditingRef = useRef(false);

  const validation = useMemo(() => {
    if (!value || value.trim() === "" || value.trim() === '{ "$and": [] }' || value.trim() === '{ "$or": [] }') {
      return { valid: true };
    }
    return validateFilterDSL(value);
  }, [value]);

  // Sync external value changes back to structured state
  useEffect(() => {
    if (isRawEditingRef.current) {
      // Reset after the value has propagated back
      isRawEditingRef.current = false;
      return;
    }
    const parsed = parseDslToConditions(value);
    if (parsed) {
      setLogicalOp(parsed.logicalOp);
      setConditions(parsed.conditions);
    }
  }, [value]);

  // Generate DSL and call onChange
  const emitChange = useCallback(
    (op: "$and" | "$or", conds: Condition[]) => {
      const dsl = conditionsToDsl(op, conds);
      onChange(dsl);
    },
    [onChange],
  );

  const handleAddCondition = () => {
    const defaultField = fields.length > 0 ? fields[0].name : "";
    const defaultOp = defaultField ? getOperatorsForField(defaultField, fields)[0]?.value || "$eq" : "$eq";
    const newConditions = [...conditions, { field: defaultField, operator: defaultOp, value: "" }];
    setConditions(newConditions);
    emitChange(logicalOp, newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    setConditions(newConditions);
    emitChange(logicalOp, newConditions);
  };

  const handleConditionChange = (index: number, key: keyof Condition, val: string) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [key]: val };

    // If field changed, reset operator to first valid one for new field type
    if (key === "field") {
      const operators = getOperatorsForField(val, fields);
      const currentOpValid = operators.some(op => op.value === newConditions[index].operator);
      if (!currentOpValid) {
        newConditions[index].operator = operators[0]?.value || "$eq";
      }
    }

    setConditions(newConditions);
    emitChange(logicalOp, newConditions);
  };

  const handleLogicalOpChange = (op: "$and" | "$or") => {
    setLogicalOp(op);
    emitChange(op, conditions);
  };

  const handleRawJsonChange = (newValue: string) => {
    isRawEditingRef.current = true;
    onChange(newValue);
  };

  const handleClear = () => {
    setConditions([]);
    emitChange(logicalOp, []);
  };

  const hasConditions = conditions.length > 0;
  const hasValue =
    value && value.trim() !== "" && value.trim() !== '{ "$and": [] }' && value.trim() !== '{ "$or": [] }';

  return (
    <div className="space-y-3">
      {/* Header */}
      {(label || hasValue) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {label && <label className="field-label block">{label}</label>}
            {hasValue && (
              <>
                {validation.valid ? (
                  <div className="flex items-center gap-1.5">
                    <IconCheckOutline18 className="h-3.5 w-3.5 text-green-600" />
                    <p className="text-xs font-medium uppercase tracking-wider text-green-600 dark:text-green-400">
                      Valid
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <IconXmarkOutline18 className="h-3.5 w-3.5 text-red-500" />
                    <p className="text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400">
                      Invalid
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          {hasConditions && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-red-500 transition-colors hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
            >
              Clear
            </button>
          )}
        </div>
      )}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {/* AND/OR toggle */}
      {hasConditions && conditions.length > 1 && (
        <div className="border-ds-border-default bg-ds-surface-card inline-flex rounded-lg border p-0.5">
          <button
            type="button"
            onClick={() => handleLogicalOpChange("$and")}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              logicalOp === "$and"
                ? "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-zinc-100"
            }`}
          >
            AND
          </button>
          <button
            type="button"
            onClick={() => handleLogicalOpChange("$or")}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              logicalOp === "$or"
                ? "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                : "text-ds-text-subtle hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:text-zinc-100"
            }`}
          >
            OR
          </button>
        </div>
      )}
      {/* Condition rows */}
      <div className="space-y-2">
        {conditions.map((condition, index) => {
          const operators = getOperatorsForField(condition.field, fields);
          return (
            <div key={index} className="border-ds-border-default bg-ds-surface-sunken space-y-2 rounded-lg border p-2">
              {/* Top row: field + operator + remove */}
              <div className="flex items-center gap-2">
                {/* Field selector */}
                <div className="min-w-0 flex-1">
                  <Select
                    value={condition.field || "__empty__"}
                    onValueChange={v => handleConditionChange(index, "field", v === "__empty__" ? "" : v)}
                  >
                    <SelectTrigger className="h-8 w-full font-mono text-xs">
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      {condition.field === "" && <SelectItem value="__empty__">Select field</SelectItem>}
                      {fields.map(f => (
                        <SelectItem key={f.name} value={f.name}>
                          {f.name} ({f.type})
                        </SelectItem>
                      ))}
                      {/* Allow custom field if not in ABI */}
                      {fields.length > 0 && condition.field && !fields.some(f => f.name === condition.field) && (
                        <SelectItem value={condition.field}>{condition.field}</SelectItem>
                      )}
                      {fields.length === 0 && condition.field && (
                        <SelectItem value={condition.field}>{condition.field}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Operator selector */}
                <div className="flex-shrink-0">
                  <Select value={condition.operator} onValueChange={v => handleConditionChange(index, "operator", v)}>
                    <SelectTrigger className="h-8 text-xs font-medium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map(op => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => handleRemoveCondition(index)}
                  className="text-ds-text-disabled flex-shrink-0 rounded-md p-1 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  <IconTrashOutline18 className="h-3.5 w-3.5" />
                </button>
              </div>
              {/* Bottom row: value input (full width) */}
              <Input
                type="text"
                value={condition.value}
                onChange={e => handleConditionChange(index, "value", e.target.value)}
                placeholder={
                  condition.operator === "$in" || condition.operator === "$nin"
                    ? '["0x...", "0x..."]'
                    : "Value or {{variable}}"
                }
                className="px-2 py-1.5 font-mono text-xs"
              />
            </div>
          );
        })}
      </div>
      {/* Add condition button */}
      <button
        type="button"
        onClick={handleAddCondition}
        className="border-ds-neutral-300 text-ds-text-subtle dark:text-ds-text-disabled flex items-center gap-1.5 rounded-md border border-dashed px-3 py-1.5 text-xs font-medium transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 dark:border-zinc-600 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
      >
        <IconPlusOutline18 className="h-3.5 w-3.5" />
        Add Condition
      </button>
      {/* Validation error */}
      {!validation.valid && validation.error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-900 dark:bg-red-950/20">
          <IconAlertInfoOutline18 className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-xs text-red-700 dark:text-red-300">{validation.error}</p>
        </div>
      )}
      {/* Raw JSON toggle */}
      <details open={showRawJson} onToggle={e => setShowRawJson((e.target as HTMLDetailsElement).open)}>
        <summary className="text-ds-text-muted hover:text-ds-text-secondary cursor-pointer text-xs transition-colors dark:hover:text-zinc-200">
          {showRawJson ? "Hide" : "View"} raw JSON
        </summary>
        <div className="mt-2">
          <JsonEditorBase
            label=""
            value={value || '{ "$and": [] }'}
            onChange={handleRawJsonChange}
            placeholder='{ "$and": [] }'
            minHeight="120px"
            validation={validation}
            hideClear
          />
        </div>
      </details>
    </div>
  );
}
